// Centralized client for the Ananda Rewards scan-verification and
// query/complaint APIs. Replaces the legacy DialerP "AddGetScanHistory" and
// "pushCRMDealMongo" endpoints project-wide.

const BASE_URL =
  process.env.REACT_APP_ANANDA_REWARDS_API_URL ||
  'https://anandarewardsapi.erpthemes.com';

const SCANS_ENDPOINT = `${BASE_URL}/api/ananda/authenticator/scans`;
const QUERIES_ENDPOINT = `${BASE_URL}/api/ananda/authenticator/queries`;

function mapScan(scan) {
  return {
    id: scan.id ?? null,
    ip: scan.ip ?? null,
    scannedAt: scan.scannedAt ?? null,
    source: scan.source ?? null,
    userAgent: scan.userAgent ?? null,
    repeat: Boolean(scan.repeat),
  };
}

// Normalizes the raw API payload into a shape the UI can render without
// caring about the exact backend field names.
function mapScanResponse(raw) {
  const code = raw.code || {};
  const scans = Array.isArray(raw.scans) ? raw.scans : [];

  return {
    code: code.code ?? null,
    productId: code.productId ?? null,
    productName: code.productName ?? null,
    productImageUrl: code.productImageUrl ?? null,
    categoryName: code.categoryName ?? null,
    businessName: code.businessName ?? null,
    scanCount: code.scanCount ?? scans.length,
    lastScannedAt: code.lastScannedAt ?? null,
    alreadyScanned: Boolean(raw.alreadyScanned),
    previousScans: raw.previousScans ?? 0,
    message: raw.message ?? null,
    history: scans.map(mapScan),
  };
}

/**
 * Verifies a scanned/entered code against the Ananda Rewards API.
 * Always resolves (never throws) with a discriminated result object:
 *   { ok: true, data }
 *   { ok: false, kind: 'validation' | 'notfound' | 'network' | 'error', message }
 */
export async function verifyScan(code) {
  const trimmedCode = typeof code === 'string' ? code.trim() : '';

  if (!trimmedCode) {
    return {
      ok: false,
      kind: 'validation',
      message: 'Please enter a valid QR/product code.',
    };
  }

  let response;
  try {
    response = await fetch(SCANS_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      // The backend derives the caller's IP from the request itself,
      // so the frontend never needs to source or expose one.
      body: JSON.stringify({ code: trimmedCode }),
    });
  } catch (err) {
    return {
      ok: false,
      kind: 'network',
      message: 'Unable to connect to the server. Please try again.',
    };
  }

  let payload;
  try {
    payload = await response.json();
  } catch (err) {
    return {
      ok: false,
      kind: 'error',
      message: 'Unable to verify this code. Please try again.',
    };
  }

  if (!response.ok || payload?.success === false) {
    const validationMessage = payload?.errors?.[0]?.message;
    return {
      ok: false,
      kind: validationMessage ? 'validation' : 'notfound',
      message: validationMessage || payload?.message || 'Unable to verify this code.',
    };
  }

  return { ok: true, data: mapScanResponse(payload) };
}

/**
 * Submits a customer query/complaint against the Ananda Rewards API.
 * Always resolves (never throws) with a discriminated result object:
 *   { ok: true, data: { message, id } }
 *   { ok: false, kind: 'validation' | 'network' | 'error', message, fieldErrors }
 */
export async function submitQuery({ name, mobile, email, code, query }) {
  const payloadBody = {
    name: (name || '').trim(),
    mobile: (mobile || '').trim(),
    email: (email || '').trim(),
    query: (query || '').trim(),
  };
  const trimmedCode = (code || '').trim();
  if (trimmedCode) {
    payloadBody.code = trimmedCode;
  }

  if (!payloadBody.name || !payloadBody.mobile || !payloadBody.email || !payloadBody.query) {
    const fieldErrors = {};
    if (!payloadBody.name) fieldErrors.name = 'Enter your name';
    if (!payloadBody.mobile) fieldErrors.mobile = 'Enter a valid mobile number';
    if (!payloadBody.email) fieldErrors.email = 'Enter your email address';
    if (!payloadBody.query) fieldErrors.query = 'Tell us what the query is';
    return {
      ok: false,
      kind: 'validation',
      message: 'Please fill in all required fields.',
      fieldErrors,
    };
  }

  let response;
  try {
    response = await fetch(QUERIES_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payloadBody),
    });
  } catch (err) {
    return {
      ok: false,
      kind: 'network',
      message: 'Unable to connect to the server. Please try again.',
    };
  }

  let payload;
  try {
    payload = await response.json();
  } catch (err) {
    return {
      ok: false,
      kind: 'error',
      message: 'Unable to submit your query. Please try again.',
    };
  }

  if (!response.ok || payload?.success === false) {
    const fieldErrors = {};
    (payload?.errors || []).forEach((e) => {
      if (e?.field) fieldErrors[e.field] = e.message;
    });
    return {
      ok: false,
      kind: Object.keys(fieldErrors).length ? 'validation' : 'error',
      message: payload?.message || 'Unable to submit your query. Please try again.',
      fieldErrors,
    };
  }

  return {
    ok: true,
    data: {
      message: payload?.message || 'Thank you. Your query has been recorded.',
      id: payload?.query?.id ?? null,
    },
  };
}

const anandaRewardsApi = { verifyScan, submitQuery };

export default anandaRewardsApi;
